// frontend/src/pages/RequestQuotation.js  (replacement)
//
// Owner's spare-parts RFQ. Same data layer as before; the presentation moves
// onto the chrome/ink system so it matches the rest of the owner space.
//
// This also fixes text being invisible while typing. OwnerLayout sets
// `text-white` on the main area, so inputs inherited white text — but these
// inputs carried only `border p-2 rounded`, with no background, so they fell
// back to the browser default of white. White on white. Every field here now
// uses the shared inputClass, which sets both colours explicitly.

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_URL from '../api/baseURL';
import { useAuth } from '../context/AuthContext';
import VehicleIdentityCard from '../components/VehicleIdentityCard';
import {
  Page, Panel, Button, Field, Badge, EmptyState, ErrorNote, Note,
  inputClass, selectClass,
} from '../components/ui/Iparc';

const RequestQuotation = ({ token: tokenProp }) => {
  const { token: ctxToken, user } = useAuth();
  const token = tokenProp || ctxToken;
  const isWorkshop = /workshop/i.test(user?.role || '');

  const [cars, setCars] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({
    car: '',
    requestType: 'owner',
    partsRequested: [],
    selectedSuppliers: [],
    attachments: []
  });
  const [newPart, setNewPart] = useState({ name: '', quantity: 1, brand: '', reference: '' });
  const [suggestions, setSuggestions] = useState([]);
  const [param, setParam] = useState({ debounce: null });
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingCars, setLoadingCars] = useState(true);

  useEffect(() => {
    // Wait for the profile. `token` is read straight from localStorage and is
    // there on the first render, but `user` arrives from an async profile
    // fetch — so an effect keyed on the token alone runs while user is still
    // null. isWorkshop would then be false and a workshop would ask
    // /vehicles/mine, which correctly returns nothing for a workshop, and the
    // effect never re-ran to correct itself. Empty dropdown, no error.
    if (!token || !user?._id) return;

    const fetchData = async () => {
      try {
        // A workshop sources parts for cars it services, not cars it owns.
        // The server applies the same rule when the RFQ is submitted (owner,
        // assigned workshop, or workshopId), so this list and that check agree.
        const carRes = await axios.get(
          isWorkshop ? `${API_URL}/vehicles/assigned/${user._id}` : `${API_URL}/vehicles/mine`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCars(carRes.data);
        if (carRes.data.length === 1) {
          setForm(prev => ({ ...prev, car: carRes.data[0]._id }));
        }

        const supplierRes = await axios.get(`${API_URL}/suppliers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuppliers(supplierRes.data);
      } catch (err) {
        console.error('Failed to load data', err);
        setError("Impossible de charger vos véhicules ou la liste des fournisseurs.");
      }
    };
    fetchData().finally(() => setLoadingCars(false));
    // Keyed on primitives, not on the `user` object: context hands back a
    // fresh object on some renders, and an effect keyed on it would refetch
    // in a loop.
    // eslint-disable-next-line
  }, [token, user?._id, isWorkshop]);

  const selectedCar = cars.find(c => c._id === form.car) || null;

  const addPart = () => {
    if (!newPart.name.trim() || newPart.quantity < 1) return;
    setForm(prev => ({
      ...prev,
      partsRequested: [...prev.partsRequested, {
        name: newPart.name.trim(),
        quantity: newPart.quantity,
        brand: newPart.brand.trim(),
        reference: newPart.reference.trim()
      }]
    }));
    setNewPart({ name: '', quantity: 1, brand: '', reference: '' });
    setSuggestions([]);
  };

  const fetchSuggestions = async (query) => {
    if (!query || query.length < 3 || !form.car) {
      setSuggestions([]);
      return;
    }
    try {
      // API_URL already ends in /api — adding another one asks for
      // /api/api/suggest/parts, which 404s on every keystroke.
      const res = await axios.get(`${API_URL}/suggest/parts`, {
        params: { vehicleId: form.car, q: query },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(res.data.suggestions || []);
    } catch (e) {
      console.error(e);
    }
  };

  const handleNameChange = (val) => {
    setNewPart({ ...newPart, name: val });
    if (param.debounce) clearTimeout(param.debounce);
    const timeout = setTimeout(() => fetchSuggestions(val), 500);
    setParam({ debounce: timeout });
  };

  const selectSuggestion = (s) => {
    setNewPart({
      ...newPart,
      brand: s.brand || '',
      reference: s.reference || '',
    });
    setSuggestions([]);
  };

  const removePart = (index) => {
    setForm(prev => ({
      ...prev,
      partsRequested: prev.partsRequested.filter((_, i) => i !== index)
    }));
  };

  const toggleSupplier = (id) => {
    setForm(prev => ({
      ...prev,
      selectedSuppliers: prev.selectedSuppliers.includes(id)
        ? prev.selectedSuppliers.filter(s => s !== id)
        : [...prev.selectedSuppliers, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setOk('');

    if (!form.car) { setError('Sélectionnez un véhicule.'); return; }
    if (form.partsRequested.length === 0) {
      setError('Ajoutez au moins une pièce à la demande.');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/quotations`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOk('Demande envoyée aux fournisseurs.');
      setForm({ car: '', requestType: 'owner', partsRequested: [], selectedSuppliers: [], attachments: [] });
    } catch (err) {
      setError(err.response?.data?.error || "L'envoi de la demande a échoué.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page
      subtitle="Pièces détachées"
      title={isWorkshop ? 'Commander des pièces' : 'Demander un devis'}
      width="narrow"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-[18px]">
        <Panel title="Véhicule">
          {/* An empty list has a cause, and it differs by role. Saying so beats
              a dropdown that simply has nothing in it. */}
          {!loadingCars && cars.length === 0 && (
            <div className="rounded-xl border border-[#2B2724] border-l-2 border-l-[#DFA23C] bg-[#141210] px-4 py-3 mb-3.5">
              <p className="text-[12.5px] text-[#C9C3B6]">
                {isWorkshop
                  ? "Aucun véhicule ne vous est assigné. Un véhicule apparaît ici une fois reçu à l'atelier ou rattaché à votre atelier."
                  : "Vous n'avez aucun véhicule enregistré. Ajoutez-en un pour demander un devis."}
              </p>
            </div>
          )}

          <Field label="Véhicule concerné" required>
            <select
              value={form.car}
              onChange={e => setForm({ ...form, car: e.target.value })}
              className={selectClass}
              required
              disabled={cars.length === 0}
            >
              <option value="">
                {loadingCars ? 'Chargement…' : 'Sélectionnez votre véhicule'}
              </option>
              {cars.map(car => (
                // The plate leads: two vehicles of the same make and model are
                // otherwise identical entries. `make` was read here but the
                // schema field is `brand`, so no brand ever appeared.
                <option key={car._id} value={car._id}>
                  {[car.plateNumber, [car.brand || car.make, car.model].filter(Boolean).join(' '),
                    car.year && `(${car.year})`].filter(Boolean).join(' — ')}
                </option>
              ))}
            </select>
          </Field>

          {/* The VIN is what the supplier needs to pick the right part
              variant, so it is shown before the request goes out. */}
          {selectedCar && (
            <VehicleIdentityCard vehicle={selectedCar} label="Véhicule concerné" className="mt-3.5" />
          )}
        </Panel>

        <Panel title="Pièces demandées">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_90px_130px] gap-2.5 items-end">
            <Field label="Désignation" className="relative">
              <input
                type="text"
                placeholder="Ex. : plaquettes de frein avant"
                value={newPart.name}
                onChange={e => handleNameChange(e.target.value)}
                className={inputClass}
              />
              {suggestions.length > 0 && (
                <div className="absolute z-20 mt-1 w-full max-h-60 overflow-auto rounded-xl border border-[#2B2724] bg-[#141210] shadow-2xl">
                  {suggestions.map((s, idx) => (
                    <button
                      type="button"
                      key={idx}
                      onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s); }}
                      className="block w-full text-left px-2.5 py-2 hover:bg-[#1A1613] transition-colors"
                    >
                      <div className="text-[12px] text-[#F4F1EA]">
                        {s.brand || '—'}{' · '}
                        <span className="font-['Space_Grotesk',sans-serif]">{s.reference || '—'}</span>
                      </div>
                      <div className="text-[10px] text-[#6E665C] font-['Space_Grotesk',sans-serif] tabular-nums">
                        {s.count}× observé
                        {typeof s.priceMin === 'number' && ` · ${s.priceMin.toLocaleString('fr-FR')}–${(s.priceMax || 0).toLocaleString('fr-FR')} F`}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </Field>

            <Field label="Qté">
              <input
                type="number"
                min={1}
                value={newPart.quantity}
                onChange={e => setNewPart({ ...newPart, quantity: Number(e.target.value) })}
                className={`${inputClass} text-center`}
              />
            </Field>

            <Field label="Référence">
              <input
                type="text"
                placeholder="Optionnel"
                value={newPart.reference}
                onChange={e => setNewPart({ ...newPart, reference: e.target.value })}
                className={inputClass}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2.5 items-end mt-2.5">
            <Field label="Marque">
              <input
                type="text"
                placeholder="Optionnel"
                value={newPart.brand}
                onChange={e => setNewPart({ ...newPart, brand: e.target.value })}
                className={inputClass}
              />
            </Field>
            <Button as="button" type="button" onClick={addPart} disabled={!newPart.name.trim()}>
              Ajouter la pièce
            </Button>
          </div>

          <div className="mt-[18px] pt-3.5 border-t border-[#241F1B]">
            {form.partsRequested.length === 0 ? (
              <p className="text-[12px] text-[#6E665C]">Aucune pièce ajoutée pour le moment.</p>
            ) : (
              <div className="flex flex-col gap-1.5">
                {form.partsRequested.map((p, i) => (
                  <div key={i}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-xl border border-[#241F1B] bg-[#1B1815]">
                    <span className="text-[13px] text-[#F4F1EA] flex-1 truncate">
                      {p.name}
                      {p.reference && <span className="text-[#8F877B]"> · {p.reference}</span>}
                      {p.brand && <span className="text-[#8F877B]"> · {p.brand}</span>}
                    </span>
                    <span className="text-[12px] text-[#8F877B] tabular-nums shrink-0">× {p.quantity}</span>
                    <button
                      type="button"
                      onClick={() => removePart(i)}
                      className="text-[#8F877B] hover:text-[#E2574B] transition-colors p-1 shrink-0"
                      aria-label="Retirer la pièce"
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="1.8" strokeLinecap="round"><path d="M6 18 18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Panel>

        <Panel
          title="Fournisseurs"
          action={
            <span className="text-[10.5px] text-[#6E665C]">
              {form.selectedSuppliers.length === 0
                ? 'Aucun sélectionné — envoyée à tous'
                : `${form.selectedSuppliers.length} sélectionné${form.selectedSuppliers.length > 1 ? 's' : ''}`}
            </span>
          }
        >
          {suppliers.length === 0 ? (
            <EmptyState title="Aucun fournisseur disponible" />
          ) : (
            <div className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
              {suppliers.map(s => {
                const on = form.selectedSuppliers.includes(s._id);
                return (
                  <button
                    key={s._id}
                    type="button"
                    onClick={() => toggleSupplier(s._id)}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-left transition-colors ${on
                      ? 'border-[#4E4740] bg-[#1E1B17]'
                      : 'border-[#241F1B] bg-[#1B1815] hover:border-[#4E4740]'}`}
                  >
                    <span className={`w-3.5 h-3.5 rounded-[4px] border shrink-0 flex items-center justify-center ${on ? 'bg-[#F4F1EA] border-[#F4F1EA]' : 'border-[#4E4740]'}`}>
                      {on && (
                        <svg className="w-2.5 h-2.5 text-[#141210]" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>
                    <span className={`text-[13px] truncate ${on ? 'text-[#F4F1EA]' : 'text-[#C9C3B6]'}`}>
                      {s.businessName || s.name || s.email || 'Fournisseur'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <p className="text-[11px] text-[#5C554C] mt-3">
            Sans sélection, la demande est ouverte à tous les fournisseurs de la plateforme.
          </p>
        </Panel>

        {error && <ErrorNote>{error}</ErrorNote>}
        {ok && <Note tone="ok">{ok}</Note>}

        <div className="flex gap-2.5">
          <Button as="button" type="submit" variant="primary" disabled={saving}>
            {saving ? 'Envoi…' : 'Envoyer la demande'}
          </Button>
          {form.partsRequested.length > 0 && (
            <Badge tone="neutral" className="self-center">
              {form.partsRequested.length} pièce{form.partsRequested.length > 1 ? 's' : ''}
            </Badge>
          )}
        </div>
      </form>
    </Page>
  );
};

export default RequestQuotation;
